import { Flex } from "@chakra-ui/layout";
import { AllocationBasisPreviewTable } from "features/fund-family/setup/fund-structure/AllocationBasisPreviewTable";
import { AllocationRuleGraphFormFields } from "features/fund-family/setup/fund-structure/AllocationRuleGraphFormFields";
import { CustomAllocationRuleInputModal } from "features/fund-family/setup/fund-structure/CustomAllocationRuleInputModal";
import { useAllocationRuleGraphForm } from "features/fund-family/setup/fund-structure/hooks/useAllocationRuleGraphForm";
import OptOutsModal from "features/fund-family/setup/fund-structure/OptOutsModal";
import React from "react";
import { useTranslation } from "react-i18next";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { useBoolean } from "usehooks-ts";

export const AllocationRuleGraphCreatePage: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["allocation"]);

  const {
    form,
    ruleGraphData,
    upsertDraftGraphAndUse,
    onSaveEdgeWeightOverrides,
    onFinalize,
    toFundStructurePage,
    onCancel,
    isCustomRuleGraph,
    shouldRefresh,
  } = useAllocationRuleGraphForm(fundFamilyId, false);

  const {
    value: isOptOutsModalOpen,
    setFalse: onOptOutsModalClose,
    setTrue: onOptOutsModalOpen,
  } = useBoolean(false);

  const {
    value: isCustomRuleModalOpen,
    setFalse: onCustomRuleModalClose,
    setTrue: onCustomRuleModalOpen,
  } = useBoolean(false);

  return (
    <>
      {ruleGraphData && (
        <OptOutsModal
          onSaveOptOuts={onSaveEdgeWeightOverrides}
          allocationRuleGraphVersionId={ruleGraphData.graphVersionId}
          isOpen={isOptOutsModalOpen}
          onModalClose={onOptOutsModalClose}
          edgeOverrideAmounts={form.watch("edgeOverrideAmounts") ?? []}
        />
      )}
      {isCustomRuleModalOpen && (
        <CustomAllocationRuleInputModal
          isOpen={isCustomRuleModalOpen}
          onModalClose={onCustomRuleModalClose}
          onSubmit={onSaveEdgeWeightOverrides}
          edgeOverrideAmounts={form.watch("edgeOverrideAmounts") ?? []}
        />
      )}
      <PageContent>
        <Form
          form={form}
          onSubmit={onFinalize}
          onSuccess={toFundStructurePage}
          onSaveProgress={toFundStructurePage}
          successMessage={t("allocation:ruleGraphCreate.success")}
        >
          <PageHeader title={t("allocation:ruleGraphCreate.title")} size="lg" />
          <PageBody>
            <Flex w="100%" gap={12}>
              <AllocationRuleGraphFormFields
                fundFamilyId={fundFamilyId}
                onAddOptOuts={onOptOutsModalOpen}
                isOptOutsEnabled={!!ruleGraphData}
                onAddCustomEdgeWeightOverrides={onCustomRuleModalOpen}
                isCustomRuleGraph={isCustomRuleGraph}
                edgeOverrideAmounts={form.watch("edgeOverrideAmounts") ?? []}
              />
              <AllocationBasisPreviewTable
                requiresRefresh={shouldRefresh}
                allocationRuleGraphVersionId={ruleGraphData?.graphVersionId}
                transactionCalculationId={form.watch("calculationId")}
                onRefresh={upsertDraftGraphAndUse}
                isCustomRuleGraph={isCustomRuleGraph}
              />
            </Flex>
          </PageBody>
          <FormFooter>
            <FormFooterActions>
              <FormCancelButton onClick={onCancel} />
              <FormSubmitButton
                isDisabled={
                  !(
                    ruleGraphData?.graphfundFamilyEventId &&
                    ruleGraphData.useFundFamilyEventId
                  )
                }
              >
                {t("allocation:ruleGraphCreate.submit")}
              </FormSubmitButton>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageContent>
    </>
  );
};
